"use client";
import {
  BMButton,
  BMComponentsType,
  BMDivider,
  BMElevationType,
  BMPrimitives,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import IcSystemDownload from "@traveloka/icon-kit-web/react/IcSystemDownload";
import { useMemo, useRef, useState } from "react";
import { Image, Pressable, StyleSheet, View, ViewStyle } from "react-native";
import { EXPORT_CSV_FILE, EXPORT_PDF_FILE } from "../../../assets/analytics";
import { useMobileContentResource } from "../../../core/resource/MobileResourceProvider";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import BMView from "../../View/BMView";

type FileOption = {
  type: "PDF" | "CSV";
  title: string;
  description: string;
  onPress: () => void;
};

type Props = {
  options: FileOption[];
  disabled: boolean;
  loading: boolean;
};

const ICON_SIZE = 24;

// @ts-expect-error -- sizes the dropdown to its content instead of shrinking to fit the zero-width positioning wrapper
const CARD_WIDTH_STYLE: ViewStyle = { width: "max-content" };

export default function AnalyticsDownloadExportOptions({
  options,
  disabled,
  loading,
}: Props) {
  const [active, setActive] = useState(false);
  const { BMComponents, BMElevation } = useBMTheme();
  const { CorporateTransactionDataAnalytics: cr } = useMobileContentResource();

  const ref = useRef<View>(null);
  useOnClickOutside(ref, () => setActive(false));

  const styles = useMemo(
    () => createStyles(BMComponents, BMElevation),
    [BMComponents, BMElevation]
  );

  const resolve = useMemo(() => {
    const icons = {
      PDF: EXPORT_PDF_FILE,
      CSV: EXPORT_CSV_FILE,
    };

    return options.map((option) => ({
      ...option,
      icon: icons[option.type],
    }));
  }, [options]);

  return (
    <BMView ref={ref} align="end">
      <BMButton
        loading={loading}
        label={cr.download}
        onPress={() => setActive(!active)}
        TrailingIcon={() => (
          <BMView style={styles.marginLeftXs}>
            <IcSystemDownload
              color={
                disabled || loading
                  ? BMComponents.BMUiButtonInkDisabled
                  : BMComponents.BMUiButtonInkPrimary
              }
            />
          </BMView>
        )}
        size="LARGE"
        disabled={disabled || loading}
      />
      {active && (
        <BMView style={styles.wrapper}>
          <BMView style={[styles.card, CARD_WIDTH_STYLE]}>
            <BMView style={styles.header}>
              <BMText variant="titleNormal">{cr.fileType}</BMText>
            </BMView>
            <BMDivider type="SUBTLE" />
            {resolve.map((option, idx) => (
              <Item option={option} key={`${option.title}-${idx}`} />
            ))}
          </BMView>
        </BMView>
      )}
    </BMView>
  );
}

function Item({ option }: { option: FileOption & { icon: string } }) {
  const { BMComponents, BMElevation } = useBMTheme();

  const [hovered, setHovered] = useState(false);

  const styles = useMemo(
    () => createStyles(BMComponents, BMElevation),
    [BMComponents, BMElevation]
  );

  return (
    <Pressable
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      onPress={option.onPress}
    >
      <BMView style={[styles.container, hovered && styles.hoveredContainer]}>
        <BMView row align="center" spacing="sm" style={styles.content}>
          <Image
            source={{
              uri: option.icon,
            }}
            alt={option.title}
            style={styles.icon}
          />
          <BMView>
            <BMText variant="uiLarge">{option.title}</BMText>
            <BMText variant="uiNormalLi" ink="BMStyleTextInkSecondary">
              {option.description}
            </BMText>
          </BMView>
        </BMView>
        <BMDivider type="SUBTLE" />
      </BMView>
    </Pressable>
  );
}

const createStyles = (
  BMComponents: BMComponentsType,
  BMElevation: BMElevationType
) =>
  StyleSheet.create({
    card: {
      right: 0,
      position: "absolute",
      marginTop: BMPrimitives.BMSize.md,
      backgroundColor: BMComponents.BMStyleCardBgcolor,
      borderRadius: BMComponents.BMStyleCardRadiusCompact,
      paddingTop: BMPrimitives.BMSize.xl,
      paddingBottom: BMPrimitives.BMSize.sm,
      ...BMElevation.elevation2,
    },
    header: {
      paddingHorizontal: BMPrimitives.BMSize.lg,
      paddingBottom: BMPrimitives.BMSize.lg,
    },
    container: {
      paddingTop: BMPrimitives.BMSize.lg,
      paddingBottom: BMPrimitives.BMSize.sm,
      paddingHorizontal: BMPrimitives.BMSize.lg,
    },
    hoveredContainer: {
      backgroundColor: BMComponents.BMStyleSurfaceBgcolorHighlight,
    },
    icon: {
      width: ICON_SIZE,
      height: ICON_SIZE,
    },
    content: { marginBottom: BMPrimitives.BMSize.sm },
    wrapper: {
      position: "relative",
    },
    marginLeftXs: {
      marginLeft: BMPrimitives.BMSize.xs,
    },
  });
